import React, { useState, useRef, useEffect, useMemo } from 'react';
import { LuChevronDown, LuSearch, LuX, LuCheck } from 'react-icons/lu';

const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  style = {},
  triggerStyle = {},
  menuStyle = {},
  allowClear = false,
  emptyMessage = 'No options found',
  menuAlign = 'left'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase().trim();
    return options.filter(opt => {
      const lbl = String(opt.label || '').toLowerCase();
      const sub = String(opt.subLabel || '').toLowerCase();
      return lbl.includes(q) || sub.includes(q);
    });
  }, [options, search]);

  const selectedOption = useMemo(() => {
    return options.find(opt => String(opt.value) === String(value));
  }, [options, value]);

  const handleSelect = (val) => {
    if (onChange) onChange(val);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) onChange('');
    setSearch('');
  };

  const positionStyles = menuAlign === 'right' 
    ? { right: 0, left: 'auto', minWidth: 'max(100%, 240px)' }
    : { left: 0, minWidth: 'max(100%, 240px)' };

  return (
    <div 
      ref={containerRef} 
      style={{ position: 'relative', width: '100%', ...style }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && isOpen) {
          setIsOpen(false);
          setSearch('');
        }
      }}
    >
      {/* Trigger Box */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          width: '100%',
          minHeight: '38px',
          padding: '6px 12px',
          background: disabled ? '#f3f4f6' : '#ffffff',
          border: isOpen ? '1.5px solid #3b5bfd' : '1px solid #d1d5db',
          borderRadius: '6px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          boxSizing: 'border-box',
          fontSize: '13px',
          color: selectedOption ? '#111827' : '#9ca3af',
          transition: 'all 0.15s ease',
          boxShadow: isOpen ? '0 0 0 2px rgba(1, 157, 136, 0.15)' : 'none',
          ...triggerStyle
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left', fontWeight: selectedOption ? 500 : 400 }}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          {allowClear && value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              style={{
                background: 'none',
                border: 'none',
                padding: '2px',
                cursor: 'pointer',
                color: '#9ca3af',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Clear selection"
            >
              <LuX size={14} />
            </button>
          )}
          <LuChevronDown
            size={16}
            color="#6b7280"
            style={{
              transition: 'transform 0.2s ease',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </div>
      </div>

      {/* Dropdown Menu Popup */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            backgroundColor: '#ffffff',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            zIndex: 1050,
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            boxSizing: 'border-box',
            ...positionStyles,
            ...menuStyle
          }}
        >
          {/* Search Input Box */}
          <div style={{ position: 'relative' }}>
            <LuSearch size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 10px 6px 30px',
                fontSize: '13px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                outline: 'none',
                boxSizing: 'border-box',
                backgroundColor: '#f8fafc'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options List */}
          <div style={{ overflowY: 'auto', maxHeight: '220px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {allowClear && (
              <div
                onClick={() => handleSelect('')}
                style={{
                  padding: '7px 10px',
                  fontSize: '12.5px',
                  cursor: 'pointer',
                  borderRadius: '5px',
                  color: '#64748b',
                  fontStyle: 'italic',
                  backgroundColor: !value ? '#f1f5f9' : 'transparent'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = !value ? '#f1f5f9' : 'transparent')}
              >
                {placeholder}
              </div>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = String(value) === String(opt.value);
                return (
                  <div
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    style={{
                      padding: '7px 10px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      borderRadius: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontWeight: isSelected ? 600 : 400,
                      backgroundColor: isSelected ? '#e6f4f1' : 'transparent',
                      color: isSelected ? '#3b5bfd' : '#1e293b'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div>
                      <div>{opt.label}</div>
                      {opt.subLabel && (
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>
                          {opt.subLabel}
                        </div>
                      )}
                    </div>
                    {isSelected && <LuCheck size={14} color="#3b5bfd" />}
                  </div>
                );
              })
            ) : (
              <div style={{ padding: '12px 8px', fontSize: '12.5px', color: '#94a3b8', textAlign: 'center' }}>
                {emptyMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
